import { useEffect, useRef, useState } from "react"

import { Button } from "@coval/ui"
import { Pause, Play } from "lucide-react"

import { AudioSeekSlider } from "./AudioSeekSlider"

function finiteDuration(seconds: number): number {
  return Number.isFinite(seconds) ? seconds : 0
}

export default function MiniAudioPlayer({
  audioUrl,
  name,
  recordingId,
}: {
  audioUrl: string
  /** The recording's name, used to tell this player's seek control from others on the page. */
  name?: string
  /**
   * A stable identity for the recording. A signed URL can be re-minted for the
   * same recording, and that keeps the seek control, and its focus, in place.
   * Defaults to the URL.
   */
  recordingId?: string
}) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  // Keyed by recording, so a different recording starts at zero with no
  // duration until its own metadata loads.
  const recordingKey = recordingId ?? audioUrl
  // A new source is loading again until its own data arrives.
  const [loadingUrl, setLoadingUrl] = useState(audioUrl)
  if (loadingUrl !== audioUrl) {
    setLoadingUrl(audioUrl)
    setIsLoading(true)
  }
  const [playback, setPlayback] = useState({
    recordingKey,
    duration: 0,
    currentTime: 0,
  })
  const isCurrentPlayback = playback.recordingKey === recordingKey
  const duration = isCurrentPlayback ? playback.duration : 0
  const currentTime = isCurrentPlayback ? playback.currentTime : 0
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0
  const [error, setError] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)
  const playButtonRef = useRef<HTMLButtonElement>(null)
  const seekControlRef = useRef<HTMLDivElement>(null)
  const sliderHadFocusRef = useRef(false)
  const showSeekSlider = duration > 0 && !error

  // Focus the user places anywhere but the slider ends any pending recovery.
  useEffect(() => {
    const handleFocusIn = (event: FocusEvent) => {
      if (
        event.target instanceof Node &&
        !seekControlRef.current?.contains(event.target)
      ) {
        sliderHadFocusRef.current = false
      }
    }
    document.addEventListener("focusin", handleFocusIn)
    return () => document.removeEventListener("focusin", handleFocusIn)
  }, [])

  // The slider leaves the page when the audio fails or changes. Focus that was
  // on it would drop to the document, so it returns to the slider if the slider
  // comes back first, or else moves to the play button once that is enabled.
  useEffect(() => {
    if (!sliderHadFocusRef.current) {
      return
    }
    const active = document.activeElement
    if (active && active !== document.body) {
      return
    }
    if (showSeekSlider) {
      seekControlRef.current
        ?.querySelector<HTMLElement>('[role="slider"]')
        ?.focus()
      return
    }
    if (isLoading) {
      return
    }
    sliderHadFocusRef.current = false
    playButtonRef.current?.focus()
  }, [showSeekSlider, isLoading])

  const handleTimeUpdate = () => {
    const audio = audioRef.current
    if (audio) {
      setPlayback({
        recordingKey,
        duration: finiteDuration(audio.duration),
        currentTime: audio.currentTime,
      })
    }
  }

  const togglePlay = async () => {
    if (audioRef.current) {
      try {
        setError(false)
        if (isPlaying) {
          audioRef.current.pause()
        } else {
          const audio = audioRef.current
          if (audio.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
            setError(true)
            setIsLoading(false)
            return
          }

          if (audio.readyState === 0) {
            setIsLoading(true)
            audio.load()
          }

          if (audio.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
            setError(true)
            setIsLoading(false)
            return
          }

          await audio.play()
        }
        setIsPlaying(!isPlaying)
      } catch {
        setError(true)
        setIsLoading(false)
      }
    }
  }

  const seekTo = (seconds: number) => {
    const audio = audioRef.current
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) {
      return
    }
    audio.currentTime = seconds
    setPlayback({
      recordingKey,
      duration: audio.duration,
      currentTime: seconds,
    })
  }

  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      audioRef.current &&
      !isNaN(audioRef.current.duration) &&
      audioRef.current.duration > 0
    ) {
      const progressBar = e.currentTarget
      const rect = progressBar.getBoundingClientRect()
      const clickPosition = (e.clientX - rect.left) / rect.width
      const newTime = clickPosition * audioRef.current.duration

      if (!isNaN(newTime) && isFinite(newTime) && newTime >= 0) {
        seekTo(newTime)
      }
    }
  }

  return (
    <div className="group flex items-center gap-2">
      <Button
        ref={playButtonRef}
        icon
        onClick={() => {
          void togglePlay()
        }}
        className={`min-w-8 w-8 h-8 ${error ? "bg-red-100 text-red-500" : ""}`}
        disabled={isLoading}
        tooltip={isPlaying ? "Pause audio" : "Play audio"}
      >
        {isPlaying ? <Pause size={16} /> : <Play size={16} />}
      </Button>
      <div
        ref={seekControlRef}
        className="relative"
        onFocus={() => {
          sliderHadFocusRef.current = true
        }}
        onBlur={(event) => {
          // Focus leaving a slider that is still on the page was the user's
          // move. A removed slider is handled by the effect above. Some
          // browsers blur during removal, so the check waits for it to finish.
          const target = event.target
          queueMicrotask(() => {
            if (target.isConnected) {
              sliderHadFocusRef.current = false
            }
          })
        }}
      >
        <div
          role="presentation"
          className={`w-24 group-hover:w-32 group-hover:h-2 group-focus-within:w-32 group-focus-within:h-2 h-1 ${
            error ? "bg-red-100" : "bg-surface-1"
          } rounded-full overflow-hidden cursor-pointer transition-all duration-200`}
          onClick={handleProgressBarClick}
        >
          <div
            className={`h-full ${
              error ? "bg-red-500" : "bg-coval-progress"
            } transition-all duration-100 ease-linear`}
            style={{ width: `${progress}%` }}
          />
        </div>
        {showSeekSlider && (
          <AudioSeekSlider
            currentTime={currentTime}
            duration={duration}
            getCurrentTime={() => audioRef.current?.currentTime ?? 0}
            onSeek={seekTo}
            label={name ? `Seek ${name}` : undefined}
          />
        )}
      </div>
      <audio
        ref={audioRef}
        src={audioUrl}
        onEnded={() => setIsPlaying(false)}
        onLoadedMetadata={(event) => {
          setPlayback({
            recordingKey,
            duration: finiteDuration(event.currentTarget.duration),
            currentTime: 0,
          })
        }}
        onLoadedData={() => {
          setIsLoading(false)
          setError(false)
        }}
        onError={() => {
          setIsLoading(false)
          setIsPlaying(false)
          setError(true)
        }}
        onTimeUpdate={handleTimeUpdate}
        className="hidden"
      />
    </div>
  )
}
